import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, getDoc, addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { resolveInviteToken } from '../lib/invites';
import { addGroupMember } from '../lib/firestore/groups';
import { createRSVP } from '../lib/firestore/events';
import { autoJoinDefaults } from '../lib/firestore/autoJoin';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../styles/Login.css';

const AuthModal = ({ isOpen, onClose, eventId, onAuthSuccess }) => {
  const [isSignup, setIsSignup] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('+1');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check for invite token
  const [inviteToken, setInviteToken] = useState(null);
  
  useEffect(() => {
    if (isOpen) {
      // Check for invite token
      const storedToken = sessionStorage.getItem('pendingInviteToken');
      if (storedToken) {
        setInviteToken(storedToken);
      }
    }
  }, [isOpen]);

  // Convert phone number to email format for Firebase Auth
  const phoneToEmail = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return `${digits}@hotelia.local`;
  };

  // Process invite token - handles all invite types (group, event, contact)
  const processInviteToken = async (token, newUserId, newUserPhone, newUserEmail) => {
    try {
      const invite = await resolveInviteToken(token);
      
      if (!invite) {
        console.log('No valid invite token found');
        return;
      }

      // Handle different invite types
      if (invite.target.kind === 'group_join') {
        const memberData = {
          userId: newUserId,
          role: invite.metadata?.role || 'member',
          joinedAt: new Date(),
        };
        
        const sponsorId = invite.metadata?.sponsorId || invite.issuedBy;
        if (sponsorId && typeof sponsorId === 'string') {
          memberData.sponsoredBy = sponsorId;
        }
        
        await addGroupMember(invite.target.id, memberData);
      } else if (invite.target.kind === 'event_join') {
        // Check if there's a stored RSVP choice
        const storedRSVP = sessionStorage.getItem('pendingRSVP');
        let rsvpState = 'going';
        
        if (storedRSVP) {
          if (storedRSVP === 'going') {
            rsvpState = 'going';
          } else if (storedRSVP === 'maybe') {
            rsvpState = 'interested';
          } else if (storedRSVP === 'no') {
            rsvpState = 'cancelled';
          }
          sessionStorage.removeItem('pendingRSVP');
        } else {
          rsvpState = invite.metadata?.role === 'guest' ? 'interested' : 'going';
        }
        
        await createRSVP({
          userId: newUserId,
          eventId: invite.target.id,
          state: rsvpState,
          plusOnes: 0,
        });
      } else if (invite.target.kind === 'contact_invite') {
        const inviterUserId = invite.target.id;
        const inviterDoc = await getDoc(doc(db, 'users', inviterUserId));
        if (!inviterDoc.exists()) {
          return;
        }
        
        const inviterData = inviterDoc.data();
        const contactsRef = collection(db, 'users', newUserId, 'contacts');
        const inviterPhoneE164 = inviterData.phone || inviterData.phoneNumber;
        
        if (inviterPhoneE164) {
          const existingQuery = query(
            contactsRef,
            where('phoneE164', '==', inviterPhoneE164.startsWith('+') ? inviterPhoneE164 : `+${inviterPhoneE164}`)
          );
          const existing = await getDocs(existingQuery);
          
          if (existing.empty) {
            await addDoc(contactsRef, {
              name: `${inviterData.firstName || ''} ${inviterData.lastName || ''}`.trim() || 'Friend',
              phoneE164: inviterPhoneE164.startsWith('+') ? inviterPhoneE164 : `+${inviterPhoneE164}`,
              email: inviterData.email || null,
              note: `Invited you to Hotelia`,
              createdAt: new Date(),
              updatedAt: new Date(),
            });
          }
        }
      }
    } catch (error) {
      console.error('Error processing invite token:', error);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!smsConsent) {
      setError('You must consent to receive SMS messages to create an account.');
      return;
    }
    
    setLoading(true);

    try {
      const authEmail = phoneToEmail(phoneNumber);
      const userCredential = await createUserWithEmailAndPassword(auth, authEmail, password);
      
      const phoneE164 = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber.replace(/\D/g, '')}`;
      
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        phone: phoneE164,
        phoneNumber: phoneE164.replace(/\D/g, ''),
        email: email || null,
        createdAt: new Date(),
        firstName: '',
        lastName: '',
        profilePicture: '',
        smsConsent: true,
        smsConsentDate: new Date(),
        smsPreference: 'all',
      });

      if (email) {
        await updateProfile(userCredential.user, {
          email: email,
        });
      }

      // Process invite token if present
      if (inviteToken) {
        await processInviteToken(inviteToken, userCredential.user.uid, phoneE164, email);
      }

      // Auto-join default groups and events
      try {
        await autoJoinDefaults(userCredential.user.uid);
      } catch (autoJoinError) {
        console.error('Error auto-joining defaults:', autoJoinError);
        // Don't block signup if auto-join fails
      }

      // If there's a pending RSVP for this event, apply it
      if (eventId) {
        const storedRSVP = sessionStorage.getItem('pendingRSVP');
        if (storedRSVP) {
          let rsvpState = 'going';
          if (storedRSVP === 'going') {
            rsvpState = 'going';
          } else if (storedRSVP === 'maybe') {
            rsvpState = 'interested';
          } else if (storedRSVP === 'no') {
            rsvpState = 'cancelled';
          }
          
          await createRSVP({
            userId: userCredential.user.uid,
            eventId: eventId,
            state: rsvpState,
            plusOnes: 0,
          });
          sessionStorage.removeItem('pendingRSVP');
        }
      }

      onAuthSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const email = phoneToEmail(phoneNumber);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // If there's a pending RSVP for this event, apply it
      if (eventId) {
        const storedRSVP = sessionStorage.getItem('pendingRSVP');
        if (storedRSVP) {
          let rsvpState = 'going';
          if (storedRSVP === 'going') {
            rsvpState = 'going';
          } else if (storedRSVP === 'maybe') {
            rsvpState = 'interested';
          } else if (storedRSVP === 'no') {
            rsvpState = 'cancelled';
          }
          
          await createRSVP({
            userId: userCredential.user.uid,
            eventId: eventId,
            state: rsvpState,
            plusOnes: 0,
          });
          sessionStorage.removeItem('pendingRSVP');
        }
      }

      // Process invite token if present
      if (inviteToken) {
        const phoneE164 = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber.replace(/\D/g, '')}`;
        await processInviteToken(inviteToken, userCredential.user.uid, phoneE164, email);
      }

      onAuthSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  console.log('?? [AuthModal] Render check:', { isOpen, eventId });
  
  if (!isOpen) {
    console.log('?? [AuthModal] Not rendering - isOpen is false');
    return null;
  }

  console.log('?? [AuthModal] ====== RENDERING MODAL ======');
  console.log('?? [AuthModal] Event ID:', eventId);
  console.log('?? [AuthModal] Modal should be visible now');
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>�</button>
        
        <div className="auth-header">
          <h1>Hotelia</h1>
          <p className="auth-header-apply">
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSe_3QyrJO0C6cNm7UZwqXZM_yIgb9GIcCvSK8_dpCa8DVq8WQ/viewform"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
            >
              Apply for Hotelia 2026
            </a>
          </p>
          <p className="auth-subtitle">{isSignup ? 'Create your account' : 'Sign in to your account'}</p>
        </div>

        {isSignup ? (
          <form onSubmit={handleSignup} className="auth-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <PhoneInput
                international
                defaultCountry="US"
                value={phoneNumber}
                onChange={setPhoneNumber}
                className="phone-input"
                id="phone"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                className="input"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email (Optional)</label>
              <input
                type="email"
                id="email"
                className="input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  required
                />
                <span>
                  I consent to receive SMS messages from Hotelia, including event invitations, 
                  reminders, and account notifications. Message frequency varies. Message and data rates may apply. 
                  Reply STOP to opt-out at any time.{' '}
                  <a href="/sms-consent.html" target="_blank" rel="noopener noreferrer" className="link-inline">Learn more</a>.
                  <br />
                  <small style={{ marginTop: '0.5rem', display: 'block', color: '#666' }}>
                    <strong>We hate spam too!</strong> Hotelia will never spam you. Only users can send you messages.
                  </small>
                </span>
              </label>
              <small className="form-hint" style={{ marginTop: '0.5rem', display: 'block' }}>
                By checking this box, you agree to our{' '}
                <a href="/terms.html" className="link-inline">Terms of Service</a> and{' '}
                <a href="/privacy.html" className="link-inline">Privacy Policy</a>.
              </small>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="auth-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="login-phone">Phone Number</label>
              <PhoneInput
                international
                defaultCountry="US"
                value={phoneNumber}
                onChange={setPhoneNumber}
                className="phone-input"
                id="login-phone"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <input
                type="password"
                id="login-password"
                className="input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p>
            {isSignup ? (
              <>
                Already have an account?{' '}
                <button type="button" className="link-button" onClick={() => setIsSignup(false)}>
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button type="button" className="link-button" onClick={() => setIsSignup(true)}>
                  Create account
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
